import React from 'react';
import styled from 'styled-components';
import { useTheme } from '../contexts/ThemeContext';
import usePageTitle from '../hooks/usePageTitle';
import PageMeta from '../components/SEO/PageMeta';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1rem;
  min-height: 60vh;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: ${(props) => props.theme.primaryText};
  text-align: center;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: ${(props) => props.theme.secondaryText};
  text-align: center;
  margin-bottom: 2rem;
  line-height: 1.5;
`;

const Section = styled.section`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.35rem;
  font-weight: 600;
  color: ${(props) => props.theme.primaryText};
  margin-bottom: 0.5rem;
`;

const SectionBody = styled.p`
  font-size: 1rem;
  color: ${(props) => props.theme.primaryText};
  line-height: 1.65;
  margin: 0 0 0.75rem 0;
  &:last-child {
    margin-bottom: 0;
  }
`;

const BulletList = styled.ul`
  font-size: 1rem;
  color: ${(props) => props.theme.primaryText};
  line-height: 1.65;
  margin: 0.5rem 0 0.75rem 1.25rem;
  padding: 0;
  list-style-type: disc;
`;

const BulletItem = styled.li`
  margin-bottom: 0.5rem;
  &:last-child {
    margin-bottom: 0;
  }
`;

const FormulaBlock = styled.div`
  background: ${(props) => props.theme.cardBg || props.theme.secondaryBg};
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 0.5rem;
  padding: 1rem 1.25rem;
  margin: 1rem 0;
  font-family: 'Georgia', serif;
  font-size: 1.1rem;
  color: ${(props) => props.theme.primaryText};
  line-height: 1.6;
`;

const BettingStrategiesPage = () => {
  const { theme } = useTheme();
  usePageTitle('MLB Betting Strategies & Bankroll Management');

  return (
    <Container>
      <PageMeta description="Learn core sports betting strategies like bankroll management, value betting, and line shopping, and see how to apply them alongside Slick Picks' MLB player prop models." />
      <Title theme={theme}>Betting Strategies</Title>
      <Subtitle theme={theme}>
        Don&apos;t just wing it. Have a plan. These strategies pair with Slick Picks&apos; MLB player prop models
        so you can combine data-driven edges with disciplined betting habits.
      </Subtitle>

      <Section>
        <SectionTitle theme={theme}>Using These Strategies With Slick Picks</SectionTitle>
        <SectionBody theme={theme}>
          Our models surface value on MLB player props like Total Bases, Hits, and Home Runs. Use the
          frameworks below alongside today&apos;s edges from the{' '}
          <a href="/mlb/total-bases">Total Bases model</a>,{' '}
          <a href="/mlb/hr">Home Run model</a>, or curated picks on the{' '}
          <a href="/game-picks">Game Picks</a> page, and size your bets based on units instead of gut feel.
        </SectionBody>
      </Section>

      <Section>
        <SectionTitle theme={theme}>1. Unit-Based Bankroll Management</SectionTitle>
        <SectionBody theme={theme}>
          This is the most critical strategy for longevity. Instead of betting random amounts, you assign a &quot;Unit&quot; (typically 1–3% of your total bankroll) to every wager.
        </SectionBody>
        <BulletList theme={theme}>
          <BulletItem><strong>How to employ it:</strong> If your total betting budget is $1,000, your standard unit might be $10 (1%).</BulletItem>
          <BulletItem><strong>Example:</strong> For a standard game you like, you bet 1 unit ($10). For a &quot;lock&quot; or high-confidence play, you might bet 3 units ($30). This prevents a single bad loss from wiping out your entire budget.</BulletItem>
        </BulletList>
      </Section>

      <Section>
        <SectionTitle theme={theme}>2. Value Betting (+EV)</SectionTitle>
        <SectionBody theme={theme}>
          Value betting isn&apos;t about picking winners; it&apos;s about finding odds that are higher than they &quot;should&quot; be based on the actual probability of the outcome.
        </SectionBody>
        <BulletList theme={theme}>
          <BulletItem><strong>How to employ it:</strong> You calculate your own probability for an event. If your model says a team has a 60% chance to win (1.67 decimal odds) but the bookmaker is offering +100 (2.00 decimal odds), that is a Positive Expected Value (+EV) bet.</BulletItem>
          <BulletItem><strong>Example:</strong> A bookie prices a coin toss at +110 for heads. Since you know the real probability is 50/50, betting heads is a &quot;value bet&quot; because the payout is higher than the risk.</BulletItem>
        </BulletList>
      </Section>

      <Section>
        <SectionTitle theme={theme}>3. Arbitrage Betting (&quot;Arbing&quot;)</SectionTitle>
        <SectionBody theme={theme}>
          This is a risk-free strategy that exploits price discrepancies between different sportsbooks. By betting on all possible outcomes of an event across different platforms, you guarantee a profit regardless of the result.
        </SectionBody>
        <BulletList theme={theme}>
          <BulletItem><strong>How to employ it:</strong> Use odds-comparison tools to find &quot;mismatched&quot; lines where the implied probabilities add up to less than 100%.</BulletItem>
          <BulletItem><strong>Example:</strong> Bookie A: Team X to win at +110 (Bet $100 to win $110). Bookie B: Team Y to win at +110 (Bet $100 to win $110). Result: You spend $200 total, and no matter who wins, you collect $210, netting a guaranteed $10 profit.</BulletItem>
        </BulletList>
      </Section>

      <Section>
        <SectionTitle theme={theme}>4. Hedging</SectionTitle>
        <SectionBody theme={theme}>
          Hedging is placing a bet on the opposite side of your original wager to lock in a profit or reduce potential losses. This is common in futures or parlays.
        </SectionBody>
        <BulletList theme={theme}>
          <BulletItem><strong>How to employ it:</strong> When the circumstances of your original bet change in your favor, you place a &quot;counter-bet&quot; on the other side.</BulletItem>
          <BulletItem><strong>Example:</strong> You bet $50 on a team to win the championship at +1000 (potential $500 profit). They make it to the finals. You then bet $200 on their opponent to win. Now, no matter who wins the final, you walk away with a profit.</BulletItem>
        </BulletList>
      </Section>

      <Section>
        <SectionTitle theme={theme}>5. Line Shopping</SectionTitle>
        <SectionBody theme={theme}>
          This is the simplest way to increase your ROI. Different sportsbooks often have slightly different odds or point spreads for the same game.
        </SectionBody>
        <BulletList theme={theme}>
          <BulletItem><strong>How to employ it:</strong> Always check at least 3–4 sportsbooks before placing a bet.</BulletItem>
          <BulletItem><strong>Example:</strong> If you want to bet on the Chiefs -3, but another book has them at -2.5 for the same price, you take the -2.5. Over a season, that half-point difference can turn dozens of &quot;pushes&quot; into wins.</BulletItem>
        </BulletList>
      </Section>

      <Section>
        <SectionTitle theme={theme}>6. Fading the Public (Contrarian Betting)</SectionTitle>
        <SectionBody theme={theme}>
          The &quot;public&quot; often bets on popular teams, favorites, and &quot;overs.&quot; Sportsbooks adjust lines to account for this heavy lopsided action, often creating value on the unpopular side.
        </SectionBody>
        <BulletList theme={theme}>
          <BulletItem><strong>How to employ it:</strong> Look for games where the majority of the bets (ticket count) are on one side, but the line is moving in the opposite direction (Reverse Line Movement). This indicates &quot;Sharp&quot; money is on the underdog.</BulletItem>
          <BulletItem><strong>Example:</strong> 80% of people are betting on the Cowboys to cover -7, but the line drops to -6. This suggests professional bettors are hammering the opponent, making the opponent the &quot;sharp&quot; play.</BulletItem>
        </BulletList>
      </Section>

      <Section>
        <SectionTitle theme={theme}>7. Matched Betting</SectionTitle>
        <SectionBody theme={theme}>
          This strategy uses the free bets and promotions offered by sportsbooks to &quot;unlock&quot; cash with zero risk.
        </SectionBody>
        <BulletList theme={theme}>
          <BulletItem><strong>How to employ it:</strong> You place a &quot;Back&quot; bet (that something will happen) at a sportsbook to trigger a promotion, and simultaneously place a &quot;Lay&quot; bet (that it won&apos;t happen) on a betting exchange.</BulletItem>
          <BulletItem><strong>Example:</strong> A bookie offers a &quot;Bet $50, Get $50 Free&quot; deal. You bet $50 on the Lakers to win at the bookie and $50 on the Lakers to not win at an exchange. You lose a few cents in fees, but you&apos;ve now unlocked a $50 free bet which you can repeat the process with to turn into real cash.</BulletItem>
        </BulletList>
      </Section>

      <Section>
        <SectionTitle theme={theme}>Important Reminder: The Kelly Criterion</SectionTitle>
        <SectionBody theme={theme}>
          For those interested in the math of betting, the Kelly Criterion is a formula used to determine the optimal size of a series of bets. It helps maximize the logarithm of wealth and is widely used by professional gamblers to decide exactly how much of their bankroll to risk based on their &quot;edge.&quot;
        </SectionBody>
        <FormulaBlock theme={theme}>
          <strong>f* = (bp − q) / b</strong>
          <br /><br />
          Where:
          <BulletList theme={theme}>
            <BulletItem><strong>f*</strong> is the fraction of the bankroll to bet.</BulletItem>
            <BulletItem><strong>b</strong> is the odds received on the bet (e.g., +110 is 1.1).</BulletItem>
            <BulletItem><strong>p</strong> is the probability of winning.</BulletItem>
            <BulletItem><strong>q</strong> is the probability of losing (1 − p).</BulletItem>
          </BulletList>
        </FormulaBlock>
      </Section>
    </Container>
  );
};

export default BettingStrategiesPage;
